import { cva } from "@-/com.npmjs.cva";
import { cn } from "@-/com.npmjs.cn";
import { Platform } from "@-/com.npmjs.react-native";

export const buttonVariants = cva({
  base: cn(
    "group shrink-0 flex-row items-center justify-center gap-2 rounded-xs shadow-none",
    Platform.select({
      web: "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive whitespace-nowrap outline-none transition-all focus-visible:ring-[3px] disabled:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
    }),
  ),
  compoundVariants: [
    {
      className: cn(
        "bg-primary active:bg-primary/90",
        Platform.select({ web: "hover:bg-primary/90" }),
      ),
      isLoading: false,
      variant: "default",
    },
    {
      className: cn(
        "bg-destructive active:bg-destructive/90 dark:bg-destructive/60",
        Platform.select({
          web: "hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        }),
      ),
      isLoading: false,
      variant: "destructive",
    },
    {
      className: cn(
        "active:bg-accent dark:active:bg-accent",
        Platform.select({ web: "hover:bg-accent dark:hover:bg-accent" }),
      ),
      isLoading: false,
      variant: "ghost",
    },
    {
      className: cn(
        "border border-border bg-background active:bg-accent dark:border-input dark:bg-input/30 dark:active:bg-accent",
        Platform.select({
          web: "hover:bg-accent dark:hover:bg-accent",
        }),
      ),
      isLoading: false,
      variant: "outline",
    },
    {
      className: cn(
        "border border-border bg-secondary active:bg-secondary/80",
        Platform.select({ web: "hover:bg-secondary/80" }),
      ),
      isLoading: false,
      variant: "secondary",
    },
    {
      className: "bg-primary/90",
      isLoading: true,
      variant: "default",
    },
    {
      className: "bg-destructive/90 dark:bg-destructive/60",
      isLoading: true,
      variant: "destructive",
    },
    {
      className: "bg-accent dark:bg-accent/50",
      isLoading: true,
      variant: "ghost",
    },
    {
      className: "border border-border bg-accent dark:border-input dark:bg-input/50",
      isLoading: true,
      variant: "outline",
    },
    {
      className: "bg-secondary/80",
      isLoading: true,
      variant: "secondary",
    },
  ],
  defaultVariants: {
    isLoading: false,
    size: "default",
    variant: "default",
  },
  variants: {
    isLoading: {
      false: "",
      true: "",
    },
    size: {
      default: cn("h-10 px-4 py-2 sm:h-9", Platform.select({ web: "has-[>svg]:px-3" })),
      // No fixed height — pair with a sized parent and `absolute inset-0` (see
      // MorphemeDetailsTile). Avoids `default`'s `h-10` / `sm:h-9` breaking square tiles on web.
      fill: "p-0",
      icon: "size-10 sm:size-9",
      lg: cn("h-11 rounded-xs px-6 sm:h-10", Platform.select({ web: "has-[>svg]:px-4" })),
      sm: cn("h-9 gap-1.5 rounded-xs px-3 sm:h-8", Platform.select({ web: "has-[>svg]:px-2.5" })),
      xs: "h-7 gap-1 rounded-xs px-2.5",
    },
    variant: {
      default: "",
      destructive: "",
      ghost: "",
      link: "",
      outline: "",
      secondary: "",
    },
  },
});

export const buttonTextVariants = cva({
  base: cn(
    "text-sm font-medium text-foreground",
    Platform.select({ web: "pointer-events-none transition-colors" }),
  ),
  defaultVariants: {
    size: "default",
    variant: "default",
  },
  variants: {
    size: {
      default: "",
      fill: "",
      icon: "",
      lg: "",
      sm: "",
      xs: "",
    },
    variant: {
      default: "text-primary-foreground",
      destructive: "text-white",
      ghost: "group-active:text-accent-foreground",
      link: cn(
        "text-primary group-active:underline",
        Platform.select({ web: "underline-offset-4 hover:underline group-hover:underline" }),
      ),
      outline: cn(
        "group-active:text-accent-foreground",
        Platform.select({ web: "group-hover:text-accent-foreground" }),
      ),
      secondary: "text-secondary-foreground",
    },
  },
});
